import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/apiConfig';
import { CronJobRun } from '../types/cron';
import { CheckCircleIcon, XCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

const CronStatusIndicator: React.FC = () => {
  const [status, setStatus] = useState<'success' | 'failed' | 'partial' | 'loading'>('loading');

  const fetchLatestRuns = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/cron-runs/latest/');
      const runs: CronJobRun[] = response.data;

      // Determine overall status
      if (runs.length === 0) {
        setStatus('success'); // No runs yet, assume OK
        return;
      }

      const hasFailures = runs.some(run => run.status === 'failed');
      const hasPartial = runs.some(run => run.status === 'partial');

      if (hasFailures) {
        setStatus('failed');
      } else if (hasPartial) {
        setStatus('partial');
      } else {
        setStatus('success');
      }
    } catch (error) {
      console.error('Failed to fetch cron status:', error);
      setStatus('failed');
    }
  }, []);

  useEffect(() => {
    fetchLatestRuns();

    // Refresh every 60 seconds
    const interval = setInterval(fetchLatestRuns, 60000);
    return () => clearInterval(interval);
  }, [fetchLatestRuns]);

  const getStatusConfig = () => {
    switch (status) {
      case 'success':
        return {
          icon: CheckCircleIcon,
          text: 'All cron jobs running successfully',
          color: COLORS.status.success,
          bgColor: '#ECFDF5',
          borderColor: '#A7F3D0',
        };
      case 'failed':
        return {
          icon: XCircleIcon,
          text: 'Cron job failures detected',
          color: COLORS.status.error,
          bgColor: '#FEF2F2',
          borderColor: '#FECACA',
        };
      case 'partial':
        return {
          icon: ExclamationCircleIcon,
          text: 'Some cron jobs have issues',
          color: COLORS.status.warning,
          bgColor: '#FFFBEB',
          borderColor: '#FDE68A',
        };
      default:
        return {
          icon: CheckCircleIcon,
          text: 'Loading cron status...',
          color: COLORS.text.secondary,
          bgColor: COLORS.bg.hover,
          borderColor: COLORS.border.default,
        };
    }
  };

  const config = getStatusConfig();
  const StatusIcon = config.icon;

  const linkStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: SPACING[12],
    paddingRight: SPACING[12],
    paddingTop: SPACING[4],
    paddingBottom: SPACING[4],
    borderRadius: RADIUS,
    border: `1px solid ${config.borderColor}`,
    backgroundColor: config.bgColor,
    textDecoration: 'none',
    transition: 'opacity 0.15s ease-in-out',
  };

  const iconStyle: React.CSSProperties = {
    height: '16px',
    width: '16px',
    marginRight: SPACING[4],
    color: config.color,
  };

  const textStyle: React.CSSProperties = {
    fontSize: TYPOGRAPHY.size.xs,
    fontWeight: TYPOGRAPHY.weight.medium,
    fontFamily: TYPOGRAPHY.body.fontFamily,
    color: config.color,
  };

  return (
    <Link
      to="/cron-monitor"
      style={linkStyle}
      title="View cron job details"
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = '0.8';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = '1';
      }}
    >
      <StatusIcon style={iconStyle} />
      <span style={textStyle}>{config.text}</span>
    </Link>
  );
};

export default CronStatusIndicator;
